import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { breadcrumb } from "./seo";

const VALUES: { title: string; body: string }[] = [
  {
    title: "Ship the calm version",
    body: "We build software that feels serene to use. Fewer knobs, clearer states, no surprises — for our customers and for each other.",
  },
  {
    title: "Evidence over opinion",
    body: "Every hiring decision our product informs is backed by evidence. We hold our own work to the same bar.",
  },
  {
    title: "Automate the busywork",
    body: "If a human is doing something a machine should, we fix it. We'd rather spend our judgment where it matters.",
  },
  {
    title: "Fairness is a feature",
    body: "Structured, consistent, integrity-checked interviews give every candidate the same fair shot. That's the whole point.",
  },
];

const ROLES: { title: string; team: string; location: string; type: string }[] = [
  { title: "Founding Full-Stack Engineer", team: "Engineering", location: "Remote · Europe", type: "Full-time" },
  { title: "ML / Evaluation Engineer", team: "Engineering", location: "Remote", type: "Full-time" },
  { title: "Design Engineer", team: "Design", location: "Remote · Europe", type: "Full-time" },
  { title: "Developer Advocate", team: "Growth", location: "Remote", type: "Full-time" },
  { title: "Founding GTM Lead", team: "Go-to-market", location: "Remote · US", type: "Full-time" },
];

const PERKS: string[] = [
  "Fully remote, async-first",
  "Meaningful equity",
  "Top-tier hardware",
  "Learning budget",
  "4-day deep-work weeks",
  "Team off-sites in the mountains",
];

export function Careers() {
  return (
    <MarketingPage
      seo={{
        title: "Careers — WonderfulInterview",
        description:
          "Help us make hiring fair, fast and human. Join a small, senior, fully-remote team building calm software and a fairer way to interview.",
        path: "/careers",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Careers", path: "/careers" },
        ]),
      }}
    >
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)" }}
        />
        <Container className="relative pt-20 pb-12 text-center sm:pt-28">
          <Eyebrow>Careers</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Help us make hiring <span className="font-display italic font-normal text-warm-accent">fair, fast and human</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            We're a small, senior team rethinking how the world interviews. If you like hard problems, calm
            products and high trust, you'll fit right in.
          </p>
          <div className="mt-8 flex justify-center">
            <MButton href="#openings" className="px-6 py-3 text-base">
              See open roles <ArrowRight />
            </MButton>
          </div>
        </Container>
        <Container className="relative pb-4">
          <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img
              src={IMG.team}
              alt="A diverse group of young professionals collaborating and smiling in a warm modern office"
              className="h-[300px] w-full object-cover sm:h-[440px]"
            />
          </div>
        </Container>
      </section>

      {/* Values */}
      <section className="py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>How we work</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">What we value.</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <h3 className="text-xl font-semibold text-warm-ink">{v.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{v.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Perks band */}
      <section className="bg-warm-sand/40 py-16">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {PERKS.map((p) => (
              <span
                key={p}
                className="rounded-full border border-warm-border bg-warm-card px-4 py-2 text-sm font-medium text-warm-ink"
              >
                {p}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* Openings */}
      <section id="openings" className="py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Open roles</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">Come build with us.</h2>
          </div>
          <div className="mt-12 overflow-hidden rounded-3xl border border-warm-border">
            {ROLES.map((r, i) => (
              <a
                key={r.title}
                href="mailto:careers@wonderfulagent.ai?subject=Application"
                className={`group flex flex-col gap-3 bg-warm-card p-6 transition-colors hover:bg-warm-glow/10 sm:flex-row sm:items-center sm:justify-between ${
                  i !== ROLES.length - 1 ? "border-b border-warm-border" : ""
                }`}
              >
                <div>
                  <div className="text-lg font-semibold text-warm-ink">{r.title}</div>
                  <div className="mt-1 text-sm text-warm-muted">
                    {r.team} · {r.location} · {r.type}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-warm-accent">
                  Apply
                  <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            ))}
          </div>
          <p className="mt-8 text-center text-warm-muted">
            Don't see your role?{" "}
            <a href="mailto:careers@wonderfulagent.ai" className="font-semibold text-warm-accent">
              Tell us what you'd build
            </a>
            .
          </p>
        </Container>
      </section>
    </MarketingPage>
  );
}
